import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { WagmiProvider } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { wagmiConfig } from './config/wagmi';
import useCoinLiquidation from './coinLiquidation/useCoinLiquidation';
import { LiquidationAsset } from './coinLiquidation/types';
import { useWalletTokens } from './hooks/useWalletTokens';
import { useNativeLockUsd } from './hooks/useNativeLock';
import { useSelectAll } from './hooks/useSelectAll';
import { getAllTokens, canSelectToken } from './utils/tokenUtils';
import BuyBackSection from './components/BuyBackSection';
import { Header } from './components/Header';
import { ConnectWalletScreen } from './components/ConnectWalletScreen';
import { TabNavigation } from './components/TabNavigation';
import { LiquidationSettings } from './components/LiquidationSettings';
import { PortfolioSummary } from './components/PortfolioSummary';
import { TokenList } from './components/TokenList';
import { SellAllButton } from './components/SellAllButton';
import { SuccessModal } from './components/SuccessModal';
import { WhopPromoBanner } from './components/WhopPromoBanner';
import { getRefCode } from './utils/localStorage';
import { trackWalletConnect } from './services/analyticsService';
import { WalletType } from './types/wallet';
import { useAppConnector } from '@/hooks/useAppConnector'

const queryClient = new QueryClient();

const AppContent: React.FC = () => {
  const {
    evmAddress,
    solanaAddress,
    suiAddress,
    isSolanaAvailable,
    isSolanaConnected,
    isSuiAvailable,
    isSuiConnected,
    isEvmConnected,
    isSolanaConnecting,
    isSuiConnecting,
    connectors,
    isEvmConnecting,
    solanaWallet,
    suiWallets,
    suiWallet,
    connect,
    activeWalletType,
    disconnectWallet
  } = useAppConnector()
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'sell' | 'buyback'>('sell');
  const [nativeLockUsd, setNativeLockUsd] = useNativeLockUsd();
  const walletType: WalletType = activeWalletType || 'EVM';
  const activeAddress =
    walletType === 'SOL'
      ? solanaAddress || undefined
      : walletType === 'SUI'
      ? suiAddress || undefined
      : evmAddress;
  const isConnected =
    walletType === 'SOL' ? isSolanaConnected : walletType === 'SUI' ? isSuiConnected : isEvmConnected;


  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (activeWalletType) {
      window.localStorage.setItem('active_wallet_type', activeWalletType);
    } else {
      window.localStorage.removeItem('active_wallet_type');
    }
  }, [activeWalletType]);

  const {
    isLoading,
    typeLiquidation,
    targetCoin,
    globalLiquidationPercentage,
    globalLiquidationData,
    customLiquidation,
    receivedTotal,
    setTargetCoin,
    setGlobalLiquidationPercentage,
    setCustomLiquidation,
    onSelectCoin,
    fetchCMCTokenPrices,
    onSellAll,
  } = useCoinLiquidation(activeAddress, {
    walletType,
    solanaWallet,
    suiWallet,
  });

  const {
    walletTokenState,
    isDataFromCache,
    refreshingTokens,
    fetchWalletTokens,
    handleSwapSuccess,
  } = useWalletTokens({
    address: activeAddress,
    walletType,
    fetchCMCTokenPrices,
    setCustomLiquidation,
  });

  const { selectAll, handleSelectAll } = useSelectAll({
    walletTokenState,
    customLiquidation,
    nativeLockUsd,
    canSelectToken: (token: LiquidationAsset) => canSelectToken(token, nativeLockUsd),
    setCustomLiquidation,
  });

  const tokens = useMemo(() => {
    return getAllTokens(typeLiquidation, globalLiquidationData, customLiquidation);
  }, [typeLiquidation, globalLiquidationData, customLiquidation]);

  const hasAtLeastOneChosenCoin = useMemo(() => {
    if (Object.keys(customLiquidation).length === 0) {
      return false;
    }
    return tokens.length > 0;
  }, [customLiquidation, tokens]);

  const walletTotalTokens = walletTokenState.tokensList.length;
  const walletTotalValue = walletTokenState.tokensList.reduce(
    (sum, token) => sum + (parseFloat(token.stringUSDBalance as string) || 0),
    0,
  );

  // console.log("33", walletTokenState.tokensList)
  // console.log("444", walletTotalValue)
  // Initialize refcode on mount - set default if not exists
  useEffect(() => {
    // This will check URL, then localStorage, and set default if not found
    getRefCode();
  }, []);

  useEffect(() => {
    if (!activeAddress) return;
    trackWalletConnect(
      activeAddress,
      walletTotalValue,
      walletType === 'SOL' ? 'sol' : walletType === 'SUI' ? 'sui' : 'evm',
    );
  }, [activeAddress, walletTotalValue, walletType]);

  const onSwapSuccess = useCallback(async (data: any) => {
    const result = await handleSwapSuccess(data, customLiquidation);
    setSuccessData(result);
    setShowSuccessModal(true);
  }, [handleSwapSuccess, customLiquidation]);

  useEffect(() => {
    const handleSwapSuccessEvent = (event: any) => {
      onSwapSuccess(event.detail).then().catch();
    };

    window.addEventListener('swap:success', handleSwapSuccessEvent);
    return () => {
      window.removeEventListener('swap:success', handleSwapSuccessEvent);
    };
  }, [onSwapSuccess]);


  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header
        isConnected={isConnected}
        address={activeAddress}
        walletType={walletType}
        onDisconnect={disconnectWallet}
      />

      <main className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
        <div className="mb-4 sm:mb-6">
          <WhopPromoBanner />
        </div>
        {!isConnected ? (
          <ConnectWalletScreen
            isConnecting={isEvmConnecting}
            connectors={connectors}
            onConnect={(connector) => connect('evm', connector)}
            isSolanaConnecting={isSolanaConnecting}
            onConnectSolana={() => connect('sol')}
            solanaAvailable={isSolanaAvailable}
            isSuiConnecting={isSuiConnecting}
            onConnectSui={(wallet) => connect('sui', wallet)}
            suiAvailable={isSuiAvailable}
            suiWallets={suiWallets}
          />
        ) : (
          <div className="space-y-4 sm:space-y-6 md:space-y-8">
            <TabNavigation
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />

            {activeTab === 'buyback' && activeAddress && (
              <BuyBackSection walletAddress={activeAddress} walletType={walletType} />
            )}

            {activeTab === 'sell' && (
              <>
                <LiquidationSettings
                  typeLiquidation={typeLiquidation}
                  targetCoin={targetCoin}
                  globalLiquidationPercentage={globalLiquidationPercentage}
                  nativeLockUsd={nativeLockUsd}
                  onTargetCoinChange={setTargetCoin}
                  onGlobalLiquidationPercentageChange={setGlobalLiquidationPercentage}
                  onNativeLockUsdChange={setNativeLockUsd}
                />

                <PortfolioSummary
                  walletTotalTokens={walletTotalTokens}
                  walletTotalValue={walletTotalValue}
                  selectedTokensCount={tokens.length}
                  receivedTotal={receivedTotal}
                  isLoading={isLoading}
                />

                <TokenList
                  walletTokenState={walletTokenState}
                  customLiquidation={customLiquidation}
                  nativeLockUsd={nativeLockUsd}
                  isDataFromCache={isDataFromCache}
                  selectAll={selectAll}
                  onSelectCoin={onSelectCoin}
                  onRefresh={() => fetchWalletTokens(true)}
                  onSelectAll={handleSelectAll}
                />

                <SellAllButton
                  tokensCount={tokens.length}
                  walletTotalTokens={walletTotalTokens}
                  isLoading={isLoading}
                  refreshingTokens={refreshingTokens}
                  typeLiquidation={typeLiquidation}
                  hasAtLeastOneChosenCoin={hasAtLeastOneChosenCoin}
                  onSellAll={onSellAll}
                />

                {showSuccessModal && successData && (
                  <SuccessModal
                    successData={successData}
                    onClose={() => setShowSuccessModal(false)}
                    onViewBuyBack={() => {
                      setShowSuccessModal(false);
                      setActiveTab('buyback');
                    }}
                  />
                )}
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <AppContent />
      </QueryClientProvider>
    </WagmiProvider>
  );
};

export default App;
